import React, {useEffect, useRef} from 'react';
import Animated, {LayoutAnimationConfig} from 'react-native-reanimated';
import {useIsFocused} from '@react-navigation/native';
import {makeStyles} from '@rneui/themed';
import {observer} from 'mobx-react';

import BaseTile from '../../components/articleTile/BaseTile';
import LoadingImg from '../../components/LoadingImg';
import ContentView from '../../components/ui-controls/layout/ContentView';
import {FOOTER_PARENT_STYLE, SiteFooter, usePageBleed} from '../../components/WebFooter/SiteFooter';
import {useScrollCollapseHandler} from '../../hooks/useScrollCollapse';
import {useTabBarBottomPadding} from '../../hooks/useTabBarBottomPadding';
import {useWebScreenDimensions} from '../../hooks/useWebScreenDimensions';
import {AppRoute} from '../../models/AppRoute';
import {RouteNavigatorScreenProps} from '../../models/navigationTypes';
import {useStores} from '../../stores/use-stores';

const ExclusiveOfferListScreen = observer(
  ({navigation}: RouteNavigatorScreenProps<AppRoute.EXCLUSIVE_OFFER_LIST>) => {
    const {offersStore, shopSelectionStore, rewardsStore} = useStores();

    const {isWebDesktopScreen} = useWebScreenDimensions();
    const pageBleed = usePageBleed();
    const tabBarPadding = useTabBarBottomPadding({skipFocusReset: true});
    const listRef = useRef(null);
    const {scrollHandler} = useScrollCollapseHandler(listRef);

    const styles = useStyles();
    const isFocused = useIsFocused();

    // Redirect to home if no rewards, only while this list is focused. The list stays mounted
    // under the offer screen, and a logout there must not pull the member to home (DIS-1914).
    useEffect(() => {
      if (isFocused && !rewardsStore.hasRewards) {
        navigation.navigate(AppRoute.HOME);
      }
    }, [navigation, isFocused, rewardsStore.hasRewards]);

    return (
      <ContentView testID={'exclusive-offer-list-screen'} parentStyle={FOOTER_PARENT_STYLE}>
        <Animated.ScrollView
          style={pageBleed}
          ref={listRef}
          onScroll={scrollHandler}
          scrollEventThrottle={16}
          showsVerticalScrollIndicator={isWebDesktopScreen}
          showsHorizontalScrollIndicator={isWebDesktopScreen}
          contentContainerStyle={{paddingBottom: tabBarPadding}}>
          {shopSelectionStore.storeLoading || offersStore.isLoading || offersStore.data === null ? (
            <LoadingImg />
          ) : null}
          {!shopSelectionStore.storeLoading &&
            !offersStore.isLoading &&
            offersStore.data !== null && (
              <>
                <LayoutAnimationConfig skipEntering>
                  {offersStore.data?.map(offer => (
                    <BaseTile
                      key={offer.offerId}
                      item={{
                        id: offer.offerId,
                        heroImage: offer.heroImage?.[0],
                        heroImageWeb: offer.heroImageDesktop?.[0],
                        headline: offer.headline,
                        bannerHeadline: offer.tileHeadline,
                        description: offer.description,
                      }}
                      customStyles={styles}
                      pressCallback={() => {
                        navigation.navigate(AppRoute.EXCLUSIVE_OFFER_DETAIL, {
                          offerId: offer.offerId,
                        });
                      }}
                      includeByline={true}
                      isWebDesktopScreen={isWebDesktopScreen}
                    />
                  )) || null}
                </LayoutAnimationConfig>
                <SiteFooter />
              </>
            )}
        </Animated.ScrollView>
      </ContentView>
    );
  },
);

const useStyles = makeStyles(() => ({
  container: {
    marginHorizontal: 0,
  },
  touchable: {
    marginBottom: 8,
  },
  textHolder: {
    paddingHorizontal: 12,
    paddingVertical: 8,
  },
  headline: {
    marginTop: 4,
    marginBottom: 4,
  },
}));

export default ExclusiveOfferListScreen;
